const defaultKeyRulesSection = {
  title: '',
  intro: '',
  rules: [] as Array<{ title: string; text: string; featured: boolean }>,
};

const defaultFeesSection = {
  title: '',
  feeIntro: '',
  feeHeader: ['', ''] as [string, string],
  fees: [] as Array<{ label: string; amount: string; note: string }>,
  feeTotalLabel: '',
  feeTotal: '',
};

const defaultRelatedServicesSection = {
  title: '',
  subtitle: '',
  links: [] as Array<{ name: string; link: string }>,
};

const defaultPopularServicesSection = {
  title: '',
  services: [] as Array<{
    title: string;
    description: string;
    slug: string;
    image: string;
  }>,
};

const defaultTimelineTable: string[][] = [
  ['', ''],
  ['', ''],
];

export function parsePackageExtrasFormFromRecord(record?: {
  keyRulesSection?: {
    title?: string;
    intro?: string;
    rules?: Array<{ title?: string; text?: string; featured?: boolean }>;
  };
  feesSection?: {
    title?: string;
    feeIntro?: string;
    feeHeader?: [string, string];
    fees?: Array<{ label?: string; amount?: string; note?: string }>;
    feeTotalLabel?: string;
    feeTotal?: string;
  };
  relatedServicesSection?: {
    title?: string;
    subtitle?: string;
    links?: Array<{ name?: string; link?: string }>;
  };
  benefitsSection?: {
    title?: string;
    subtitle?: string;
    benefits?: string[];
  };
  popularServicesSection?: {
    title?: string;
    services?: Array<{
      title?: string;
      description?: string;
      slug?: string;
      image?: string;
    }>;
  };
  relatedArticlesSection?: {
    title?: string;
    subtitle?: string;
    articles?: Array<{
      name?: string;
      description?: string;
      url?: string;
      externalLink?: string;
      readTime?: string;
    }>;
  };
  timelineName?: string | null;
  timelineTable?: string[][];
}) {
  const keyRules = record?.keyRulesSection || {};
  const fees = record?.feesSection || {};
  const relatedServices = record?.relatedServicesSection || {};
  const relatedArticles = record?.relatedArticlesSection || {};
  const benefitsSec = record?.benefitsSection || {};
  const popularServices = record?.popularServicesSection || {};
  const rawTimelineTable = record?.timelineTable;
  const timelineTable =
    Array.isArray(rawTimelineTable) && rawTimelineTable.length
      ? rawTimelineTable
      : defaultTimelineTable;

  return {
    keyRulesSection: {
      title: keyRules.title || defaultKeyRulesSection.title,
      intro: keyRules.intro || '',
      rules:
        Array.isArray(keyRules.rules) && keyRules.rules.length
          ? keyRules.rules.map((rule) => ({
              title: rule.title || '',
              text: rule.text || '',
              featured: rule.featured === true,
            }))
          : defaultKeyRulesSection.rules,
    },
    feesSection: {
      title: fees.title || defaultFeesSection.title,
      feeIntro: fees.feeIntro || defaultFeesSection.feeIntro,
      feeHeader: fees.feeHeader || defaultFeesSection.feeHeader,
      fees:
        Array.isArray(fees.fees) && fees.fees.length
          ? fees.fees.map((fee) => ({
              label: fee.label || '',
              amount: fee.amount || '',
              note: fee.note || '',
            }))
          : defaultFeesSection.fees,
      feeTotalLabel: fees.feeTotalLabel || defaultFeesSection.feeTotalLabel,
      feeTotal: fees.feeTotal || defaultFeesSection.feeTotal,
    },
    relatedServicesSection: {
      title: relatedServices.title || defaultRelatedServicesSection.title,
      subtitle: relatedServices.subtitle || defaultRelatedServicesSection.subtitle,
      links:
        Array.isArray(relatedServices.links) && relatedServices.links.length
          ? relatedServices.links.map((link) => ({
              name: link.name || '',
              link: link.link || '',
            }))
          : defaultRelatedServicesSection.links,
    },
    benefitsSection: {
      title: benefitsSec.title || '',
      subtitle: benefitsSec.subtitle || '',
      benefits: Array.isArray(benefitsSec.benefits) ? benefitsSec.benefits : [],
    },
    popularServicesSection: {
      title: popularServices.title || defaultPopularServicesSection.title,
      services:
        Array.isArray(popularServices.services) && popularServices.services.length
          ? popularServices.services.map((service) => ({
              title: service.title || '',
              description: service.description || '',
              slug: service.slug || '',
              image: service.image || '',
            }))
          : defaultPopularServicesSection.services,
    },
    relatedArticlesSection: {
      title: relatedArticles.title || '',
      subtitle: relatedArticles.subtitle || '',
      articles:
        Array.isArray(relatedArticles.articles) && relatedArticles.articles.length
          ? relatedArticles.articles.map((article) => ({
              name: article.name || '',
              description: article.description || '',
              url: article.url || '',
              externalLink: article.externalLink || '',
              readTime: article.readTime || '',
            }))
          : [],
    },
    timelineName: record?.timelineName || '',
    timelineTable,
  };
}

export function buildPackageExtrasPayload(values: {
  keyRulesSection?: {
    title?: string;
    intro?: string;
    rules?: Array<{ title?: string; text?: string; featured?: boolean }>;
  };
  feesSection?: {
    title?: string;
    feeIntro?: string;
    feeHeader?: [string, string];
    fees?: Array<{ label?: string; amount?: string; note?: string }>;
    feeTotalLabel?: string;
    feeTotal?: string;
  };
  relatedServicesSection?: {
    title?: string;
    subtitle?: string;
    links?: Array<{ name?: string; link?: string }>;
  };
  benefitsSection?: {
    title?: string;
    subtitle?: string;
    benefits?: string[];
  };
  popularServicesSection?: {
    title?: string;
    services?: Array<{
      title?: string;
      description?: string;
      slug?: string;
      image?: string;
    }>;
  };
  relatedArticlesSection?: {
    title?: string;
    subtitle?: string;
    articles?: Array<{
      name?: string;
      description?: string;
      url?: string;
      externalLink?: string;
      readTime?: string;
    }>;
  };
  timelineName?: string;
  timelineTable?: string[][];
}) {
  return {
    keyRulesSection: {
      title: values.keyRulesSection?.title?.trim() || '',
      intro: values.keyRulesSection?.intro?.trim() || '',
      rules: (values.keyRulesSection?.rules || [])
        .filter((rule) => rule?.title?.trim() && rule?.text?.trim())
        .map((rule) => ({
          title: rule.title!.trim(),
          text: rule.text!.trim(),
          featured: rule.featured === true,
        })),
    },
    feesSection: {
      title: values.feesSection?.title?.trim() || '',
      feeIntro: values.feesSection?.feeIntro?.trim() || '',
      feeHeader: values.feesSection?.feeHeader,
      fees: (values.feesSection?.fees || [])
        .filter((fee) => fee?.label?.trim() && fee?.amount?.trim())
        .map((fee) => ({
          label: fee.label!.trim(),
          amount: fee.amount!.trim(),
          note: fee.note?.trim() || undefined,
        })),
      feeTotalLabel: values.feesSection?.feeTotalLabel?.trim() || '',
      feeTotal: values.feesSection?.feeTotal?.trim() || '',
    },
    relatedServicesSection: {
      title: values.relatedServicesSection?.title?.trim() || '',
      subtitle: values.relatedServicesSection?.subtitle?.trim() || '',
      links: (values.relatedServicesSection?.links || [])
        .filter((link) => link?.name?.trim() && link?.link?.trim())
        .map((link) => ({
          name: link.name!.trim(),
          link: link.link!.trim(),
        })),
    },
    benefitsSection: {
      title: values.benefitsSection?.title?.trim() || '',
      subtitle: values.benefitsSection?.subtitle?.trim() || '',
      benefits: (values.benefitsSection?.benefits || [])
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean),
    },
    popularServicesSection: {
      title: values.popularServicesSection?.title?.trim() || '',
      services: (values.popularServicesSection?.services || [])
        .filter(
          (service) => service?.title?.trim() && service?.description?.trim() && service?.slug?.trim()
        )
        .map((service) => ({
          title: service.title!.trim(),
          description: service.description!.trim(),
          slug: service.slug!.trim(),
          image: service.image?.trim() || '',
        })),
    },
    relatedArticlesSection: {
      title: values.relatedArticlesSection?.title?.trim() || '',
      subtitle: values.relatedArticlesSection?.subtitle?.trim() || '',
      articles: (values.relatedArticlesSection?.articles || [])
        .filter(
          (article) => article?.name?.trim() && article?.description?.trim() && article?.url?.trim()
        )
        .map((article) => ({
          name: article.name!.trim(),
          description: article.description!.trim(),
          url: article.url!.trim(),
          externalLink: article.externalLink?.trim() || '',
          readTime: article.readTime?.trim() || '',
        })),
    },
    // Timeline is saved via the existing Package/Service Timeline APIs,
    // not on process-FAQ management records.
  };
}
